import React from 'react';

import { cssMap } from '@compiled/react';
import noop from 'lodash/noop';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import Form from '@atlaskit/form/form';
import { FormHeader } from '@atlaskit/form/form-header';

import { Text } from '@atlaskit/primitives/compiled/text';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { defineMessage, useIntl } from '@atlassian/jira-intl';

import { lookupTypeByTypeId } from './TypeDefinition.types.tsx';
import ProductCard from './ProductCard.tsx';
import {
	getDefaultCreationState,
	generateApplinksCreationRedirectionUrl,
} from './getApplinksAdminUrl.tsx';
import type { StageReciprocalPromptProps } from './StageProps.types.tsx';
import { StatusLozenge } from './StatusLozenge.tsx';
import { ERRORS } from './Errors.tsx';
import { SubpageTemplate } from './SubpageTemplate.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
	fullContainer: {
		height: '100%',
	},
});

const cancelButtonTextNonfinalMessage = defineMessage({
	defaultMessage: 'Cancel',
	id: 'applinks.administration.ui.creation-flow-dialog.cancel-form-button-text-nonfinal',
	description: 'The button text for cancelling creation of the applink.',
});

const reciprocalPromptContinueOnButtonTextNonfinalMessage = defineMessage({
	defaultMessage: 'Continue on {targetApplicationName}',
	id: 'applinks.administration.ui.creation-flow-dialog.review-oauth-1-reciprocal-description-nonfinal',
	description:
		'Button label indicating the user will be redirected to {targetApplicationName} to continue the creation of the link. {targetApplicationName} will be replaced with the user-defined name of the remote site. It could also be "Jira" or "Confluence".',
});

const reciprocalPromptDescriptionNonfinalMessage = defineMessage({
	defaultMessage:
		'A link from Jira to {targetApplicationName} has been established. You will need to navigate to {targetApplicationName} to complete the link.',
	id: 'applinks.administration.ui.creation-flow-dialog.review-oauth-1-reciprocal-description-nonfinal',
	description:
		'Description of form informing the user that a one-way link has been established, and that they must navigate to the remote application to complete the two-way link. {targetApplicationName} will be replaced with the user-defined name of the remote site. It could also be "Jira" or "Confluence".',
});

type ReciprocalPromptSubpageProps = {
	state: StageReciprocalPromptProps;
};

const ReciprocalPromptSubpage = (props: ReciprocalPromptSubpageProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const thisApplicationManifest = props.state.localManifest;
	const targetApplicationManifest = props.state.remoteManifest;

	const targetCloudId = props.state.remoteManifest.id;
	const thisBaseUrl = thisApplicationManifest.url;
	const thisBaseTypeId = thisApplicationManifest.typeId;

	const creationState = getDefaultCreationState(thisBaseUrl, thisBaseTypeId, targetCloudId);

	const reciprocalLinkUrl = generateApplinksCreationRedirectionUrl(
		targetApplicationManifest.url,
		targetApplicationManifest.typeId,
		creationState,
	);

	return (
		<Form onSubmit={noop} xcss={styles.fullContainer}>
			<SubpageTemplate
				variant={props.state.variant}
				kind={props.state.kind}
				buttons={
					<ButtonGroup label="Form submit options">
						<Button appearance="subtle">{formatMessage(cancelButtonTextNonfinalMessage)}</Button>
						<LinkButton href={reciprocalLinkUrl} appearance="primary">
							{formatMessage(reciprocalPromptContinueOnButtonTextNonfinalMessage, {
								targetApplicationName: targetApplicationManifest.name,
							})}
						</LinkButton>
					</ButtonGroup>
				}
				content={
					<Stack>
						<FormHeader title="Create reciprocal link">
							<Text>
								{formatMessage(reciprocalPromptDescriptionNonfinalMessage, {
									targetApplicationName: targetApplicationManifest.name,
								})}
							</Text>
						</FormHeader>

						<Stack xcss={styles.linkDisplay} space="space.200">
							<ProductCard
								// TODO: Determine if the type is DC or Cloud
								applicationType={lookupTypeByTypeId(targetApplicationManifest.typeId, false)}
								displayUrl={targetApplicationManifest.url}
								heading="Application Link"
								statusLozenge={<StatusLozenge kind="error" err={ERRORS.CONFIGURATION_COMPLETE} />}
							/>
						</Stack>
					</Stack>
				}
			/>
		</Form>
	);
};

export default ReciprocalPromptSubpage;
